import { useEffect, useState, useMemo, useCallback } from 'react';
import { FiServer, FiPlus, FiEdit2, FiSave, FiX, FiTrash2, FiFilter, FiSearch, FiRefreshCw } from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import Pagination from '../components/Pagination';
import { CRUD_UI } from '../components/crudUi';

const Sites = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    hospital_address: '',
    hospital_phone: '',
    hospital_email: '',
    hospital_tax_code: '',
    exam_status_check_interval_ms: '',
    sync_interval_ms: '',
    sync_enabled: true,
    sync_batch_size: 50,
    sync_status_filter: 'completed',
    sync_only_with_study: true,
    sync_max_retries: 3,
    sync_retry_delay_ms: 60000,
    heartbeat_interval_ms: '',
    orthanc_url: '',
    orthanc_username: '',
    orthanc_password: '',
    orthanc_api_key: '',
    orthanc_local_ae_title: '',
    orthanc_remote_ae_title: '',
    is_active: true,
    status: 'active',
  });
  const [formErrors, setFormErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await api.get('/orthanc/sites');
      let sitesData = response.data.data || [];
      
      // Apply search filter on client side
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        sitesData = sitesData.filter(site => 
          site.id?.toLowerCase().includes(searchLower) ||
          site.name?.toLowerCase().includes(searchLower) ||
          site.hospital_address?.toLowerCase().includes(searchLower) ||
          site.hospital_phone?.toLowerCase().includes(searchLower) ||
          site.hospital_email?.toLowerCase().includes(searchLower)
        );
      }
      
      setSites(sitesData);
    } catch (error) {
      toast.error('Không thể tải danh sách cơ sở');
    } finally {
      setLoading(false);
    }
  };

  // Pagination calculations
  const totalItems = sites.length;
  const paginatedSites = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sites.slice(startIndex, endIndex);
  }, [sites, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Handle page change - use useCallback to ensure stable reference
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Handle items per page change - use useCallback to ensure stable reference
  const handleItemsPerPageChange = useCallback((value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    fetchSites();
  }, [searchTerm]);

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      hospital_address: '',
      hospital_phone: '',
      hospital_email: '',
      hospital_tax_code: '',
      exam_status_check_interval_ms: '',
      sync_interval_ms: '',
      sync_enabled: true,
      sync_batch_size: 50,
      sync_status_filter: 'completed',
      sync_only_with_study: true,
      sync_max_retries: 3,
      sync_retry_delay_ms: 60000,
      heartbeat_interval_ms: '',
      orthanc_url: '',
      orthanc_username: '',
      orthanc_password: '',
      orthanc_api_key: '',
      orthanc_local_ae_title: '',
      orthanc_remote_ae_title: '',
      is_active: true,
      status: 'active',
    });
    setFormErrors({});
    setTouchedFields({});
    setEditingSite(null);
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateInterval = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const parsed = Number(value);
    if (!Number.isInteger(parsed)) return 'Giá trị phải là số nguyên';
    if (parsed < 1000) return 'Giá trị phải >= 1000';
    return '';
  };

  const validateSiteField = (field, value) => {
    if (field === 'id') {
      if (editingSite) return '';
      if (!value?.trim()) return 'Mã site là bắt buộc';
      if (value.trim().length > 50) return 'Mã site tối đa 50 ký tự';
      if (!/^[A-Za-z0-9_-]+$/.test(value.trim())) return 'Mã site chỉ gồm chữ, số, _ và -';
      return '';
    }
    if (field === 'name') {
      if (!value?.trim()) return 'Tên cơ sở là bắt buộc';
      if (value.trim().length > 100) return 'Tên cơ sở tối đa 100 ký tự';
      return '';
    }
    if (field === 'hospital_email' && value?.trim()) {
      if (!isValidEmail(value.trim())) return 'Email không hợp lệ';
      return '';
    }
    if (field === 'exam_status_check_interval_ms' || field === 'sync_interval_ms' || field === 'heartbeat_interval_ms') {
      return validateInterval(value);
    }
    if (field === 'sync_batch_size') {
      if (value === '' || value === null || value === undefined) return '';
      const parsed = Number(value);
      if (!Number.isInteger(parsed)) return 'Giá trị phải là số nguyên';
      if (parsed < 1) return 'Giá trị phải >= 1';
      return '';
    }
    if (field === 'sync_status_filter') {
      if (!value?.trim()) return 'Status filter là bắt buộc';
      return '';
    }
    if (field === 'sync_max_retries') {
      if (value === '' || value === null || value === undefined) return '';
      const parsed = Number(value);
      if (!Number.isInteger(parsed)) return 'Giá trị phải là số nguyên';
      if (parsed < 0) return 'Giá trị phải >= 0';
      return '';
    }
    if (field === 'sync_retry_delay_ms') {
      if (value === '' || value === null || value === undefined) return '';
      const parsed = Number(value);
      if (!Number.isInteger(parsed)) return 'Giá trị phải là số nguyên';
      if (parsed < 0) return 'Giá trị phải >= 0';
      return '';
    }
    return '';
  };

  const validateSiteForm = () => {
    const errors = {};
    ['id', 'name', 'hospital_email', 'exam_status_check_interval_ms', 'sync_interval_ms', 'heartbeat_interval_ms'].forEach((field) => {
      const error = validateSiteField(field, formData[field]);
      if (error) errors[field] = error;
    });
    return errors;
  };

  const updateSiteField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prevErrors) => {
      const nextErrors = { ...prevErrors };
      const error = validateSiteField(field, value);
      if (error) nextErrors[field] = error;
      else delete nextErrors[field];
      return nextErrors;
    });
  };

  const normalizeIntervalField = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const buildSitePayload = () => ({
    ...formData,
    exam_status_check_interval_ms: normalizeIntervalField(formData.exam_status_check_interval_ms),
    sync_interval_ms: normalizeIntervalField(formData.sync_interval_ms),
    heartbeat_interval_ms: normalizeIntervalField(formData.heartbeat_interval_ms),
    sync_enabled: formData.sync_enabled,
    sync_batch_size: formData.sync_batch_size ? parseInt(formData.sync_batch_size) : 50,
    sync_status_filter: formData.sync_status_filter?.trim() || 'completed',
    sync_only_with_study: formData.sync_only_with_study,
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const errors = validateSiteForm();
      setFormErrors(errors);
      setTouchedFields({ id: true, name: true, hospital_email: true, exam_status_check_interval_ms: true, sync_interval_ms: true, heartbeat_interval_ms: true });
      if (Object.keys(errors).length > 0) {
        toast.error('Vui lòng kiểm tra lại dữ liệu');
        return;
      }

      const response = await api.post('/orthanc/sites', buildSitePayload());
      if (response.data.warning) {
        toast.warning(response.data.warning.message || 'Cơ sở đã được tạo nhưng kiểm tra kết nối thất bại');
      } else {
        toast.success('Tạo cơ sở thành công');
      }
      setShowModal(false);
      resetForm();
      fetchSites();
    } catch (error) {
      let errorMessage = 'Không thể tạo cơ sở';
      
      if (error.response?.data?.error) {
        const errorData = error.response.data.error;
        
        // Ưu tiên message chi tiết từ backend
        if (errorData.details) {
          errorMessage = errorData.details;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.errors && Array.isArray(errorData.errors)) {
          // Format từng lỗi validation
          errorMessage = errorData.errors.map(err => {
            const fieldName = err.param || err.path || 'Trường';
            const fieldLabel = getFieldLabel(fieldName);
            return `${fieldLabel}: ${err.msg}`;
          }).join('\n');
        }
      }
      
      // Hiển thị từng lỗi trên các dòng riêng nếu có nhiều lỗi
      if (errorMessage.includes('\n')) {
        errorMessage.split('\n').forEach(msg => {
          if (msg.trim()) toast.error(msg.trim());
        });
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleEdit = async (site) => {
    try {
      const response = await api.get(`/orthanc/sites/${site.id}`);
      setFormData({
        id: response.data.data.id || '',
        name: response.data.data.name || '',
        hospital_address: response.data.data.hospital_address || '',
        hospital_phone: response.data.data.hospital_phone || '',
        hospital_email: response.data.data.hospital_email || '',
        hospital_tax_code: response.data.data.hospital_tax_code || '',
        exam_status_check_interval_ms: response.data.data.exam_status_check_interval_ms ?? '',
        sync_interval_ms: response.data.data.sync_interval_ms ?? '',
        sync_enabled: response.data.data.sync_enabled !== undefined ? response.data.data.sync_enabled : true,
        sync_batch_size: response.data.data.sync_batch_size ?? 50,
        sync_status_filter: response.data.data.sync_status_filter || 'completed',
        sync_only_with_study: response.data.data.sync_only_with_study !== undefined ? response.data.data.sync_only_with_study : true,
        sync_max_retries: response.data.data.sync_max_retries ?? 3,
        sync_retry_delay_ms: response.data.data.sync_retry_delay_ms ?? 60000,
        heartbeat_interval_ms: response.data.data.heartbeat_interval_ms ?? '',
        orthanc_url: response.data.data.orthanc_url || '',
        orthanc_username: response.data.data.orthanc_username || '',
        orthanc_password: response.data.data.orthanc_password || '',
        orthanc_api_key: response.data.data.orthanc_api_key || '',
        orthanc_local_ae_title: response.data.data.orthanc_local_ae_title || '',
        orthanc_remote_ae_title: response.data.data.orthanc_remote_ae_title || '',
        is_active: response.data.data.is_active !== undefined ? response.data.data.is_active : true,
        status: response.data.data.status || 'active',
      });
      setEditingSite(site.id);
      setFormErrors({});
      setTouchedFields({});
      setShowModal(true);
    } catch (error) {
      toast.error('Không thể tải thông tin cơ sở');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const errors = validateSiteForm();
      setFormErrors(errors);
      setTouchedFields({ id: true, name: true, hospital_email: true, exam_status_check_interval_ms: true, sync_interval_ms: true, heartbeat_interval_ms: true });
      if (Object.keys(errors).length > 0) {
        toast.error('Vui lòng kiểm tra lại dữ liệu');
        return;
      }

      await api.put(`/orthanc/sites/${editingSite}`, buildSitePayload());
      toast.success('Cập nhật cơ sở thành công');
      setShowModal(false);
      resetForm();
      fetchSites();
    } catch (error) {
      let errorMessage = 'Không thể cập nhật cơ sở';
      
      if (error.response?.data?.error) {
        const errorData = error.response.data.error;
        
        // Ưu tiên message chi tiết từ backend
        if (errorData.details) {
          errorMessage = errorData.details;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.errors && Array.isArray(errorData.errors)) {
          // Format từng lỗi validation
          errorMessage = errorData.errors.map(err => {
            const fieldName = err.param || err.path || 'Trường';
            const fieldLabel = getFieldLabel(fieldName);
            return `${fieldLabel}: ${err.msg}`;
          }).join('\n');
        }
      }
      
      // Hiển thị từng lỗi trên các dòng riêng nếu có nhiều lỗi
      if (errorMessage.includes('\n')) {
        errorMessage.split('\n').forEach(msg => {
          if (msg.trim()) toast.error(msg.trim());
        });
      } else {
        toast.error(errorMessage);
      }
    }
  };

  // Helper function để chuyển đổi tên field sang tiếng Việt
  const getFieldLabel = (fieldName) => {
    const fieldLabels = {
      'id': 'Mã site',
      'name': 'Tên cơ sở',
      'hospital_address': 'Địa chỉ',
      'hospital_phone': 'Số điện thoại',
      'hospital_email': 'Email',
      'hospital_tax_code': 'Mã số thuế',
      'exam_status_check_interval_ms': 'Chu kỳ check exam (ms)',
      'sync_interval_ms': 'Chu kỳ sync DICOM (ms)',
      'heartbeat_interval_ms': 'Chu kỳ heartbeat (ms)',
      'orthanc_url': 'Orthanc URL',
      'orthanc_username': 'Tên đăng nhập Orthanc',
      'orthanc_password': 'Mật khẩu Orthanc',
      'orthanc_api_key': 'API key Orthanc',
      'orthanc_local_ae_title': 'Orthanc Local AE Title',
      'orthanc_remote_ae_title': 'Orthanc Remote AE Title',
      'status': 'Trạng thái',
      'is_active': 'Kích hoạt',
    };
    return fieldLabels[fieldName] || fieldName;
  };

  const handleDelete = async (site) => {
    if (!window.confirm(`Bạn có chắc chắn muốn xóa cơ sở "${site.name}"?\n\nLưu ý: Không thể xóa cơ sở mặc định hoặc cơ sở có dữ liệu liên quan.`)) {
      return;
    }

    setDeleting(site.id);
    try {
      await api.delete(`/orthanc/sites/${site.id}`);
      toast.success('Xóa cơ sở thành công');
      fetchSites();
    } catch (error) {
      let errorMessage = 'Không thể xóa cơ sở';
      
      if (error.response?.data?.error) {
        const errorData = error.response.data.error;
        
        if (errorData.details) {
          // Format details nếu là object
          if (typeof errorData.details === 'object') {
            const detailsText = Object.entries(errorData.details)
              .filter(([key]) => key !== 'note')
              .map(([key, value]) => {
                const keyLabels = {
                  'exams': 'Số chỉ định',
                  'sync_jobs': 'Số job đồng bộ',
                  'users': 'Số người dùng'
                };
                return `${keyLabels[key] || key}: ${value}`;
              })
              .join(', ');
            
            if (detailsText) {
              errorMessage = `${errorData.message || errorMessage}\n${detailsText}`;
              if (errorData.details.note) {
                errorMessage += `\n\n${errorData.details.note}`;
              }
            } else {
              errorMessage = errorData.message || errorMessage;
            }
          } else {
            errorMessage = errorData.details;
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      // Hiển thị lỗi (có thể nhiều dòng)
      if (errorMessage.includes('\n')) {
        const lines = errorMessage.split('\n').filter(line => line.trim());
        lines.forEach((msg, index) => {
          if (index === 0) {
            toast.error(msg.trim());
          } else {
            // Hiển thị các dòng tiếp theo sau một chút delay
            setTimeout(() => toast.error(msg.trim()), index * 200);
          }
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className={CRUD_UI.loadingWrap}>
        <div className={CRUD_UI.loadingSpinner}></div>
        <p className="text-slate-500 text-sm">Đang tải danh sách cơ sở...</p>
      </div>
    );
  }

  return (
    <div className={CRUD_UI.page}>
      {/* Search and Filters */}
      <div className={CRUD_UI.filterCard}>
        <div className={`${CRUD_UI.filterHeader} justify-between flex-wrap gap-2`}>
          <div className="flex items-center gap-2">
            <FiFilter className="text-slate-500 text-xl" />
            <span className="text-sm font-semibold text-slate-900">Tìm kiếm và bộ lọc</span>
          </div>
          <button
            className={CRUD_UI.primaryButton}
            onClick={() => { resetForm(); setShowModal(true); }}
          >
            <FiPlus className="text-base" />
            Thêm cơ sở
          </button>
        </div>
        <div className="flex gap-4 flex-wrap">
          <div className={CRUD_UI.filterFieldWide}>
            <label className={CRUD_UI.filterLabel}>Tìm kiếm</label>
            <div className={CRUD_UI.searchWrap}>
              <FiSearch className={CRUD_UI.searchIcon} />
              <input
                type="text"
                className={CRUD_UI.searchInput}
                placeholder="Tìm theo mã site, tên, địa chỉ, điện thoại, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className={CRUD_UI.searchClearButton}
                  onClick={() => setSearchTerm('')}
                  title="Xóa tìm kiếm"
                >
                  <FiX className="text-base" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={CRUD_UI.tableViewportShell}>
        <div className={CRUD_UI.tableViewportScroll}>
          <table className={CRUD_UI.table} key={`sites-table-${currentPage}-${itemsPerPage}`}>
            <thead className={CRUD_UI.tableHead}>
              <tr>
                <th className={CRUD_UI.tableHeaderCell}>Mã site</th>
                <th className={CRUD_UI.tableHeaderCell}>Tên cơ sở</th>
                <th className={CRUD_UI.tableHeaderCell}>Địa chỉ</th>
                <th className={CRUD_UI.tableHeaderCell}>Liên hệ</th>
                <th className={CRUD_UI.tableHeaderCell}>Trạng thái</th>
                <th className={CRUD_UI.tableHeaderCell}>Thao tác</th>
              </tr>
            </thead>
            <tbody key={`page-${currentPage}-items-${itemsPerPage}`}>
              {paginatedSites.length === 0 ? (
                <tr>
                  <td colSpan="7" className={CRUD_UI.emptyCell}>
                    <div className="flex flex-col items-center gap-2">
                      <FiServer className="text-5xl text-slate-300" />
                      <p className="text-slate-500 text-sm">Không tìm thấy cơ sở nào</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedSites.map((site) => (
                  <tr key={site.id} className={CRUD_UI.tableRow}>
                    <td className="px-3 py-2 text-sm">
                      <code className="px-1.5 py-0.5 rounded bg-slate-100 text-xs text-slate-700">{site.id}</code>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <span className="text-slate-900 font-medium">{site.name}</span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="text-slate-700 text-xs">
                        {site.hospital_address || '-'}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex flex-col gap-0.5 text-xs text-slate-700">
                        <span>{site.hospital_phone || '-'}</span>
                        <span className="text-slate-500">{site.hospital_email || '-'}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${
                          site.is_active && site.status === 'active'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {site.is_active && site.status === 'active' ? 'Hoạt động' : (site.status || 'inactive')}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex items-center gap-1">
                        <button
                          className={CRUD_UI.iconButtonEdit}
                          onClick={() => handleEdit(site)}
                          disabled={deleting === site.id}
                          title="Chỉnh sửa"
                        >
                          <FiEdit2 className="text-base" />
                        </button>
                        <button
                          className={CRUD_UI.iconButtonDelete}
                          onClick={() => handleDelete(site)}
                          disabled={deleting === site.id || site.is_default}
                          title={site.is_default ? 'Không thể xóa cơ sở mặc định' : 'Xóa cơ sở'}
                        >
                          {deleting === site.id ? (
                            <FiRefreshCw className="text-base animate-spin" />
                          ) : (
                            <FiTrash2 className="text-base" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalItems > 0 && (
        <div className={CRUD_UI.paginationDock}>
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[10, 20, 50, 100]}
          />
        </div>
      )}

      {showModal && (
        <div className={CRUD_UI.modalOverlay}>
          <div className={CRUD_UI.modalContainer} onClick={(e) => e.stopPropagation()}>
            <div className={CRUD_UI.modalHeader}>
              <h2 className={CRUD_UI.modalTitle}>{editingSite ? 'Chỉnh sửa Site' : 'Thêm Site'}</h2>
              <button 
                className={CRUD_UI.modalCloseButton}
                onClick={() => { setShowModal(false); resetForm(); }}
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <form onSubmit={editingSite ? handleUpdate : handleCreate} className={CRUD_UI.modalForm}>
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">Thông tin site</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Mã site *</label>
                    <input
                      type="text"
                      className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.id && formErrors.id ? 'border-red-500' : 'border-slate-300'}`}
                      value={formData.id}
                      onChange={(e) => updateSiteField('id', e.target.value)}
                      onBlur={() => setTouchedFields((prev) => ({ ...prev, id: true }))}
                      placeholder="VD: HOSPITAL_A, CLINIC_01"
                      required
                      maxLength={50}
                      disabled={!!editingSite}
                    />
                    {touchedFields.id && formErrors.id && <p className="mt-1 text-xs text-red-600">{formErrors.id}</p>}
                    <small className="text-xs text-slate-500 mt-1 block">
                      Mã site dùng trong HIS/PACS và phân quyền (VARCHAR(50)). Không đổi được sau khi tạo.
                    </small>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Tên cơ sở *</label>
                    <input
                      type="text"
                      className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.name && formErrors.name ? 'border-red-500' : 'border-slate-300'}`}
                      value={formData.name}
                      onChange={(e) => updateSiteField('name', e.target.value)}
                      onBlur={() => setTouchedFields((prev) => ({ ...prev, name: true }))}
                      placeholder="Bệnh viện A"
                      required
                    />
                    {touchedFields.name && formErrors.name && <p className="mt-1 text-xs text-red-600">{formErrors.name}</p>}
                  </div>
                </div>
                <div>
                  <small className="text-xs text-slate-500 mt-1 block">
                    Tài khoản API theo site được quản lý trong mục người dùng (role API), không cấu hình API key tại màn hình Site.
                  </small>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">Thông tin liên hệ</h3>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">Địa chỉ</label>
                  <textarea
                    className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary resize-none"
                    value={formData.hospital_address}
                    onChange={(e) => setFormData({ ...formData, hospital_address: e.target.value })}
                    placeholder="123 Đường ABC, Quận XYZ, TP.HCM"
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Số điện thoại</label>
                    <input
                      type="tel"
                      className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary"
                      value={formData.hospital_phone}
                      onChange={(e) => setFormData({ ...formData, hospital_phone: e.target.value })}
                      placeholder="0901234567"
                      maxLength={50}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Email</label>
                    <input
                      type="email"
                      className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.hospital_email && formErrors.hospital_email ? 'border-red-500' : 'border-slate-300'}`}
                      value={formData.hospital_email}
                      onChange={(e) => updateSiteField('hospital_email', e.target.value)}
                      onBlur={() => setTouchedFields((prev) => ({ ...prev, hospital_email: true }))}
                      placeholder="info@hospital.com"
                    />
                    {touchedFields.hospital_email && formErrors.hospital_email && <p className="mt-1 text-xs text-red-600">{formErrors.hospital_email}</p>}
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">Cài đặt</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Trạng thái</label>
                    <select
                      className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="active">Hoạt động</option>
                      <option value="inactive">Không hoạt động</option>
                      <option value="error">Lỗi</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Kích hoạt</label>
                    <select
                      className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary"
                      value={formData.is_active ? 'true' : 'false'}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                    >
                      <option value="true">Có</option>
                      <option value="false">Không</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Chu kỳ check exam (ms)</label>
                    <input
                      type="number"
                      min="1000"
                      step="1000"
                      className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.exam_status_check_interval_ms && formErrors.exam_status_check_interval_ms ? 'border-red-500' : 'border-slate-300'}`}
                      value={formData.exam_status_check_interval_ms}
                      onChange={(e) => updateSiteField('exam_status_check_interval_ms', e.target.value)}
                      onBlur={() => setTouchedFields((prev) => ({ ...prev, exam_status_check_interval_ms: true }))}
                      placeholder="60000"
                    />
                    {touchedFields.exam_status_check_interval_ms && formErrors.exam_status_check_interval_ms && <p className="mt-1 text-xs text-red-600">{formErrors.exam_status_check_interval_ms}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Chu kỳ sync DICOM (ms)</label>
                    <input
                      type="number"
                      min="1000"
                      step="1000"
                      className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.sync_interval_ms && formErrors.sync_interval_ms ? 'border-red-500' : 'border-slate-300'}`}
                      value={formData.sync_interval_ms}
                      onChange={(e) => updateSiteField('sync_interval_ms', e.target.value)}
                      onBlur={() => setTouchedFields((prev) => ({ ...prev, sync_interval_ms: true }))}
                      placeholder="300000"
                    />
                    {touchedFields.sync_interval_ms && formErrors.sync_interval_ms && <p className="mt-1 text-xs text-red-600">{formErrors.sync_interval_ms}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Chu kỳ heartbeat (ms)</label>
                    <input
                      type="number"
                      min="1000"
                      step="1000"
                      className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.heartbeat_interval_ms && formErrors.heartbeat_interval_ms ? 'border-red-500' : 'border-slate-300'}`}
                      value={formData.heartbeat_interval_ms}
                      onChange={(e) => updateSiteField('heartbeat_interval_ms', e.target.value)}
                      onBlur={() => setTouchedFields((prev) => ({ ...prev, heartbeat_interval_ms: true }))}
                      placeholder="60000"
                    />
                    {touchedFields.heartbeat_interval_ms && formErrors.heartbeat_interval_ms && <p className="mt-1 text-xs text-red-600">{formErrors.heartbeat_interval_ms}</p>}
                  </div>
                </div>

                <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h4 className="text-xs font-semibold text-slate-800 mb-3">Cấu hình Sync chi tiết</h4>
                  
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Bật Sync</label>
                      <select
                        className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary"
                        value={formData.sync_enabled ? 'true' : 'false'}
                        onChange={(e) => setFormData({ ...formData, sync_enabled: e.target.value === 'true' })}
                      >
                        <option value="true">Có</option>
                        <option value="false">Không</option>
                      </select>
                      <small className="text-xs text-slate-500 mt-1 block">Tự động sync DICOM lên cloud</small>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Batch Size</label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.sync_batch_size && formErrors.sync_batch_size ? 'border-red-500' : 'border-slate-300'}`}
                        value={formData.sync_batch_size}
                        onChange={(e) => updateSiteField('sync_batch_size', e.target.value)}
                        onBlur={() => setTouchedFields((prev) => ({ ...prev, sync_batch_size: true }))}
                        placeholder="50"
                      />
                      {touchedFields.sync_batch_size && formErrors.sync_batch_size && <p className="mt-1 text-xs text-red-600">{formErrors.sync_batch_size}</p>}
                      <small className="text-xs text-slate-500 mt-1 block">Số exam tối đa mỗi lần sync</small>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Status Filter</label>
                      <input
                        type="text"
                        className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.sync_status_filter && formErrors.sync_status_filter ? 'border-red-500' : 'border-slate-300'}`}
                        value={formData.sync_status_filter}
                        onChange={(e) => updateSiteField('sync_status_filter', e.target.value)}
                        onBlur={() => setTouchedFields((prev) => ({ ...prev, sync_status_filter: true }))}
                        placeholder="completed"
                      />
                      {touchedFields.sync_status_filter && formErrors.sync_status_filter && <p className="mt-1 text-xs text-red-600">{formErrors.sync_status_filter}</p>}
                      <small className="text-xs text-slate-500 mt-1 block">Status cần sync (phân cách bằng dấu phẩy)</small>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Chỉ sync có Study ID</label>
                      <select
                        className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary"
                        value={formData.sync_only_with_study ? 'true' : 'false'}
                        onChange={(e) => setFormData({ ...formData, sync_only_with_study: e.target.value === 'true' })}
                      >
                        <option value="true">Có</option>
                        <option value="false">Không</option>
                      </select>
                      <small className="text-xs text-slate-500 mt-1 block">Chỉ sync exam có study_instance_uid</small>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Max Retries</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.sync_max_retries && formErrors.sync_max_retries ? 'border-red-500' : 'border-slate-300'}`}
                        value={formData.sync_max_retries}
                        onChange={(e) => updateSiteField('sync_max_retries', e.target.value)}
                        onBlur={() => setTouchedFields((prev) => ({ ...prev, sync_max_retries: true }))}
                        placeholder="3"
                      />
                      {touchedFields.sync_max_retries && formErrors.sync_max_retries && <p className="mt-1 text-xs text-red-600">{formErrors.sync_max_retries}</p>}
                      <small className="text-xs text-slate-500 mt-1 block">Số lần thử lại tối đa khi sync thất bại</small>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Retry Delay (ms)</label>
                      <input
                        type="number"
                        min="0"
                        step="1000"
                        className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.sync_retry_delay_ms && formErrors.sync_retry_delay_ms ? 'border-red-500' : 'border-slate-300'}`}
                        value={formData.sync_retry_delay_ms}
                        onChange={(e) => updateSiteField('sync_retry_delay_ms', e.target.value)}
                        onBlur={() => setTouchedFields((prev) => ({ ...prev, sync_retry_delay_ms: true }))}
                        placeholder="60000"
                      />
                      {touchedFields.sync_retry_delay_ms && formErrors.sync_retry_delay_ms && <p className="mt-1 text-xs text-red-600">{formErrors.sync_retry_delay_ms}</p>}
                      <small className="text-xs text-slate-500 mt-1 block">Thời gian chờ giữa các lần retry</small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">Cấu hình Orthanc</h3>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">Orthanc URL</label>
                  <input
                    type="url"
                    className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary"
                    value={formData.orthanc_url}
                    onChange={(e) => setFormData({ ...formData, orthanc_url: e.target.value })}
                    placeholder="http://localhost:8042"
                  />
                  <small className="text-xs text-slate-500 mt-1 block">
                    URL của máy chủ Orthanc tại cơ sở này (VD: http://192.168.1.100:8042)
                  </small>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Tên đăng nhập Orthanc</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary"
                      value={formData.orthanc_username}
                      onChange={(e) => setFormData({ ...formData, orthanc_username: e.target.value })}
                      placeholder="orthanc"
                      autoComplete="off"
                    />
                    <small className="text-xs text-slate-500 mt-1 block">
                      Dùng khi Orthanc bật xác thực Basic Auth
                    </small>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Mật khẩu Orthanc</label>
                    <input
                      type="password"
                      className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary"
                      value={formData.orthanc_password}
                      onChange={(e) => setFormData({ ...formData, orthanc_password: e.target.value })}
                      placeholder="••••••••"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">API Key Orthanc (tuỳ chọn)</label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary"
                    value={formData.orthanc_api_key}
                    onChange={(e) => setFormData({ ...formData, orthanc_api_key: e.target.value })}
                    placeholder="••••••••"
                    autoComplete="off"
                  />
                  <small className="text-xs text-slate-500 mt-1 block">
                    Dùng nếu Orthanc bật xác thực bằng API Key thay vì Basic Auth
                  </small>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Orthanc Local AE Title *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary"
                      value={formData.orthanc_local_ae_title}
                      onChange={(e) => setFormData({ ...formData, orthanc_local_ae_title: e.target.value })}
                      placeholder="ORTHANC"
                      maxLength={100}
                    />
                    <small className="text-xs text-slate-500 mt-1 block">
                      AE Title của máy chủ Orthanc tại cơ sở (dùng cho DICOM komunikate)
                    </small>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Orthanc Remote AE Title *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary"
                      value={formData.orthanc_remote_ae_title}
                      onChange={(e) => setFormData({ ...formData, orthanc_remote_ae_title: e.target.value })}
                      placeholder="CLOUD-ORTHANC"
                      maxLength={100}
                    />
                    <small className="text-xs text-slate-500 mt-1 block">
                      AE Title của Cloud Orthanc để push DICOM studies (VD: CLOUD-ORTHANC)
                    </small>
                  </div>
                </div>
              </div>

              <div className={CRUD_UI.modalFooter}>
                <button 
                  type="button" 
                  className={CRUD_UI.secondaryButton}
                  onClick={() => { setShowModal(false); resetForm(); }}
                >
                  <FiX className="text-base" />
                  Hủy
                </button>
                <button 
                  type="submit" 
                  className={CRUD_UI.primaryButton}
                >
                  <FiSave className="text-base" />
                  {editingSite ? 'Cập nhật' : 'Tạo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sites;

